// src/lib/pwa.client.ts — PWA (F9-T1): înregistrarea service worker-ului + promptul de instalare.
//
// Partajat între aplicația internă (app.client) și viewerul public (embed.client).
// SW-ul se înregistrează DOAR în afara localhost-ului: pe dev, un SW care ține app-shell-ul
// în cache ar servi HTML stale și ar strica HMR-ul lui `bun --hot` — acolo, dimpotrivă,
// DEZînregistrăm defensiv orice SW rămas de la un test anterior.
//
// Butonul „Instalează aplicația" e gated pe `install_available`: browserul decide când oferă
// instalarea (evenimentul `beforeinstallprompt` — Chrome/Edge/Android). Pe iOS evenimentul nu
// există — instalarea se face din Safari („Adaugă pe ecranul principal"), butonul nu apare.

import van from "vanjs-core"

/** Eveniment Chrome nestandardizat — doar ce folosim din el. */
interface BeforeInstallPromptEvent extends Event {
	prompt(): Promise<void>
}

export const install_available = van.state(false)
let deferred_prompt: BeforeInstallPromptEvent | null = null

export function init_pwa(): void {
	if (typeof window === "undefined") {
		return
	}
	// Link-ul de manifest se injectează la runtime: pus în HTML, bundler-ul Bun ar încerca să
	// rezolve href-ul (absolut → eroare de build; relativ → URL hash-uit cu MIME greșit).
	// Chrome — singurul browser cu prompt de instalare — procesează manifestul adăugat dinamic.
	//
	// Pe /embed, manifestul se generează DINAMIC (blob) cu start_url = URL-ul curent CU token:
	// manifestul global are start_url "/", iar cetățeanul care instalează portalul public ar fi
	// primit o aplicație care se deschide pe landing-ul intern, fără harta lui (review 24.08).
	// Iconițele devin URL-uri absolute — un manifest blob: nu are bază de rezolvare pe origin.
	if (!document.querySelector('link[rel="manifest"]')) {
		const link = document.createElement("link")
		link.rel = "manifest"
		if (location.pathname === "/embed") {
			const manifest = {
				name: "Hartă publică — Urbanism",
				short_name: "Urbanism",
				lang: "ro",
				start_url: location.pathname + location.search,
				scope: "/",
				display: "standalone",
				background_color: "#ffffff",
				theme_color: "#4b9785",
				icons: [
					{
						src: new URL("/public/logo-192.png", location.origin).href,
						sizes: "192x192",
						type: "image/png",
					},
					{
						src: new URL("/public/logo-512.png", location.origin).href,
						sizes: "512x512",
						type: "image/png",
					},
				],
			}
			link.href = URL.createObjectURL(
				new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }),
			)
		} else {
			link.href = "/manifest.webmanifest"
		}
		document.head.appendChild(link)
	}
	if (!("serviceWorker" in navigator)) {
		return
	}
	const is_local =
		location.hostname === "localhost" ||
		location.hostname === "127.0.0.1" ||
		location.hostname === "[::1]"
	if (is_local) {
		void navigator.serviceWorker.getRegistrations().then((regs) => {
			for (const reg of regs) {
				void reg.unregister()
			}
		})
	} else {
		void navigator.serviceWorker.register("/sw.js").catch(() => {
			/* fără SW (browser vechi/eroare) — aplicația merge normal, doar fără offline */
		})
	}
	window.addEventListener("beforeinstallprompt", (e) => {
		e.preventDefault() // fără mini-infobar-ul implicit al browserului; oferim butonul nostru
		deferred_prompt = e as BeforeInstallPromptEvent
		install_available.val = true
	})
	window.addEventListener("appinstalled", () => {
		deferred_prompt = null
		install_available.val = false
	})
}

/** Deschide promptul nativ de instalare (are efect doar când `install_available` e true). */
export function prompt_install(): void {
	const p = deferred_prompt
	if (!p) {
		return
	}
	deferred_prompt = null
	install_available.val = false
	void p.prompt()
}

// ── Aplicația din Google Play (Etapa 3, publicată 31.08) ─────
// Pe Android, canalul oficial de instalare e store-ul (TWA „Urbanism by VEGO") — butoanele de
// instalare din UI trimit ACOLO în loc de promptul PWA; pe iOS/desktop rămâne PWA-ul.
export const PLAY_STORE_URL =
	"https://play.google.com/store/apps/details?id=holdings.renda.urbanism"

/** UA-ul e Android? (static per sesiune — nu are nevoie de reactivitate) */
export function is_android(): boolean {
	return /android/i.test(navigator.userAgent)
}

/** Rulăm DEJA în aplicație? (TWA din Play sau PWA instalat — display-mode standalone/fullscreen;
 *  acolo CTA-urile „descarcă aplicația" n-au sens: ești deja în ea.) Static per sesiune. */
export function is_standalone(): boolean {
	return (
		window.matchMedia("(display-mode: standalone)").matches ||
		window.matchMedia("(display-mode: fullscreen)").matches ||
		(navigator as { standalone?: boolean }).standalone === true // iOS Safari legacy
	)
}
